import type { AutomationImportSummaryDto, AutomationRunDto, AutomationTeamDto } from '@tms/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api-client';
import { queryKeys } from '../../lib/query-keys';
import { automationAccess, automationRequest } from './client';

/** How often running pipelines are re-read; the API asks GitLab on every call. */
const ACTIVE_RUNS_POLL_MS = 15_000;

/** TestRun's address and the role there. TestRun keeps the test accounts; the rest is in TMS. */
export const useAutomationAccess = () => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: [...queryKeys.automation.access, 'view'],
    queryFn: () => automationAccess(queryClient),
    retry: false,
  });
};

export const useAutomationTeams = () =>
  useQuery({
    queryKey: queryKeys.automation.teams,
    queryFn: ({ signal }) => api.get<AutomationTeamDto[]>('/automation/teams', undefined, signal),
  });

export const useAutomationActiveRuns = () =>
  useQuery({
    queryKey: queryKeys.automation.activeRuns,
    queryFn: ({ signal }) =>
      api.get<AutomationRunDto[]>('/automation/runs/active', undefined, signal),
    refetchInterval: ACTIVE_RUNS_POLL_MS,
  });

/**
 * Brings TestRun's registry over: the browser reads TestRun's export (TestRun
 * runs in another cluster, the TMS server may not reach it) and hands it to TMS.
 */
export const useImportFromTestRun = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const exported = await automationRequest<unknown>(queryClient, '/export');
      return api.post<AutomationImportSummaryDto>('/automation/import', exported);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.automation.teams });
      void queryClient.invalidateQueries({ queryKey: queryKeys.automation.activeRuns });
    },
  });
};
